"use client";

import { ArrowUpRight, Check, Phone } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

interface SafetyCardProps {
  image: string;
  title: string;
  description: string;
  readMoreLink: string;
  callNumber: string;
  tags?: string[];
}

const SafetyCard = ({
  image,
  title,
  description,
  readMoreLink,
  callNumber,
  tags = [],
}: SafetyCardProps) => {
  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[1.6rem] border border-[#0F172A]/10 bg-[#FFFFFF] shadow-[0_12px_36px_rgba(15,23,42,0.07)] transition duration-300 hover:-translate-y-1 hover:border-[#0C4A6E]/20 hover:shadow-[0_20px_44px_rgba(15,23,42,0.12)]">
      <div className="relative aspect-[16/10] overflow-hidden bg-[#E0F2FE]">
        <Image
          src={image}
          alt={title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
          className="object-cover transition duration-500 group-hover:scale-[1.035]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#082F49]/56 via-transparent to-transparent" />

      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div>
          <h3 className="text-xl leading-tight font-extrabold tracking-[-0.02em] text-[#0F172A]">
            {title}
          </h3>

          {tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-[#E0F2FE]/70 px-2.5 py-1 text-[10px] font-bold text-[#334155]"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <p className="mt-4 line-clamp-3 text-sm leading-6 text-[#475569]">
            {description}
          </p>
        </div>

        <div className="mt-auto flex items-center gap-3 pt-6">
          <Link
            href={readMoreLink}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-[#0C4A6E] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#082F49] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0C4A6E]"
          >
            View details
            <ArrowUpRight className="size-4" />
          </Link>

          <a
            href={callNumber ? `tel:${callNumber}` : "#"}
            aria-label={`Call about ${title}`}
            className="grid size-11 shrink-0 place-items-center rounded-full border border-[#0369A1]/25 bg-[#0369A1]/10 text-[#075985] transition hover:bg-[#0369A1] hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0369A1]"
          >
            <Phone className="size-4" />
          </a>
        </div>

        <p className="mt-4 flex items-center gap-2 border-t border-[#0F172A]/8 pt-4 text-[11px] font-semibold text-[#64748B]">
          <Check className="size-3.5 text-[#0369A1]" strokeWidth={3} />
          Site measurement · Service scope · Installation
        </p>
      </div>
    </article>
  );
};

export default SafetyCard;
